import React, { useState } from 'react';
import config from '../config';
import { toast } from 'react-toastify';
import s from '../pages/bookings/Bookings.module.scss';

const CALL_OUTCOMES = [
  { value: 'no_answer', label: 'No answer' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'accepted_on_call', label: 'Accepted on call' },
  { value: 'callback_later', label: 'Callback later' },
];

const REJECTION_REASONS = [
  { value: 'price_too_low', label: 'Price too low' },
  { value: 'price_too_high', label: 'Price too high' },
  { value: 'long_distance', label: 'Long distance / dead km' },
  { value: 'bad_timing', label: 'Bad timing' },
  { value: 'already_busy', label: 'Already busy' },
  { value: 'personal', label: 'Personal / not available' },
  { value: 'other', label: 'Other' },
];

const DriverOutreachModal = ({
  bookingId,
  vehicleId,
  driverName,
  isDraft = false,
  onClose,
  onSuccess,
}) => {
  const [callOutcome, setCallOutcome] = useState('no_answer');
  const [rejectionReasonCode, setRejectionReasonCode] = useState('price_too_low');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [quotedDriverPay, setQuotedDriverPay] = useState('');
  const [acceptedDriverPay, setAcceptedDriverPay] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bookingId || !vehicleId) {
      toast.error('Booking and vehicle are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const operatorName =
        localStorage.getItem('user_name') ||
        localStorage.getItem('username') ||
        'Unknown Operator';

      const payload = {
        booking_id: Number(bookingId),
        vehicle_id: Number(vehicleId),
        call_outcome: callOutcome,
        logged_by: operatorName,
        is_draft: Boolean(isDraft),
      };

      if (callOutcome === 'rejected') {
        payload.rejection_reason_code = rejectionReasonCode;
        if (rejectionNotes.trim()) payload.rejection_notes = rejectionNotes.trim();
      }

      const quoted = parseFloat(quotedDriverPay);
      if (Number.isFinite(quoted) && quoted > 0) payload.quoted_driver_pay = quoted;

      const accepted = parseFloat(acceptedDriverPay);
      if (callOutcome === 'accepted_on_call' && Number.isFinite(accepted) && accepted > 0) {
        payload.accepted_driver_pay = accepted;
      }

      const response = await fetch(`${config.dispatchApiBase}/ml/driver-outreach`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      toast.success(`Call logged for V-${vehicleId}`);
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error('Driver outreach log error:', err);
      toast.error(err.message || 'Failed to log call');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={s.modalOverlay} onClick={onClose}>
      <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <i className="fa fa-phone" style={{ color: '#2563eb', fontSize: '24px' }} />
          <h3>Log driver call</h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={s.modalBody}>
            <p className={s.modalLabel}>{isDraft ? 'Draft' : 'Booking'}</p>
            <p className={s.modalValue}>#{bookingId}</p>
            <p className={s.modalLabel}>Vehicle</p>
            <p className={s.modalValue}>
              V-{vehicleId}
              {driverName ? ` · ${driverName}` : ''}
            </p>

            <p className={s.modalLabel}>Call outcome</p>
            <select
              className={s.modalInput}
              value={callOutcome}
              onChange={(e) => setCallOutcome(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: '12px' }}
            >
              {CALL_OUTCOMES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {callOutcome === 'rejected' && (
              <>
                <p className={s.modalLabel}>Rejection reason</p>
                <select
                  className={s.modalInput}
                  value={rejectionReasonCode}
                  onChange={(e) => setRejectionReasonCode(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', marginBottom: '12px' }}
                >
                  {REJECTION_REASONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className={s.modalLabel}>Notes (optional)</p>
                <textarea
                  className={s.modalInput}
                  rows={2}
                  value={rejectionNotes}
                  onChange={(e) => setRejectionNotes(e.target.value)}
                  placeholder="Additional details..."
                  style={{ width: '100%', boxSizing: 'border-box', marginBottom: '12px' }}
                />
              </>
            )}

            <p className={s.modalLabel}>Quoted pay (₹, optional)</p>
            <input
              type="number"
              min="0"
              step="1"
              className={s.modalInput}
              value={quotedDriverPay}
              onChange={(e) => setQuotedDriverPay(e.target.value)}
              placeholder="What you offered"
              style={{ width: '100%', boxSizing: 'border-box', marginBottom: '12px' }}
            />

            {callOutcome === 'accepted_on_call' && (
              <>
                <p className={s.modalLabel}>Agreed pay (₹, optional)</p>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={s.modalInput}
                  value={acceptedDriverPay}
                  onChange={(e) => setAcceptedDriverPay(e.target.value)}
                  placeholder="What driver agreed to"
                  style={{ width: '100%', boxSizing: 'border-box', marginBottom: '12px' }}
                />
              </>
            )}
          </div>
          <div className={s.modalFooter}>
            <button type="button" className={s.modalCancelBtn} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className={s.modalConfirmBtn} disabled={saving}>
              {saving ? (
                <>
                  <i className="fa fa-spinner fa-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-check" />
                  Save call log
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverOutreachModal;
